import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useTeacherProfile } from '../../hooks/useTeacherProfile';
import { usePublicTeacherProfile } from '../../hooks/usePublicTeacherProfile';
import { fullSyncPublicProfile } from '../../services/publicProfileService';
import { TeacherLayout } from '../layouts/TeacherLayout';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase';
import { StarIcon, MapPinIcon, CheckCircleIcon, MessageSquareIcon, CalendarIcon, ClockIcon, GlobeIcon, AwardIcon, VideoIcon, UploadIcon, PlayIcon, XIcon, PencilIcon, EyeIcon, Share2Icon, CopyIcon, PlusIcon, TrashIcon, FileTextIcon, ExternalLinkIcon } from 'lucide-react';
// Mock data for demonstration
const mockTeacherData = {
  firstName: '',
  lastName: '',
  tagline: '',
  city: '',
  state: '',
  profilePhotoUrl: '',
  bannerImageUrl: '',
  rating: 0,
  reviewCount: 0,
  yearsTeaching: 0,
  totalStudents: 0,
  responseRate: 0,
  bio: '',
  teachingPhilosophy: '',
  instruments: [],
  teachingLevels: {
    beginner: false,
    intermediate: false,
    advanced: false
  },
  lessonFormats: [],
  languages: [],
  pricing: {
    isPublic: false,
    startingPrice: 0,
    currency: 'USD',
    contactForPricing: true
  },
  introVideos: [] as string[],
  credentials: [],
  reviews: [],
  availableSlots: []
};
// Star Rating Component
function StarRating({
  rating,
  size = 'sm'
}: {
  rating: number;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };
  return <div className="flex items-center gap-0.5" data-id="element-8601">
      {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className={`${sizeClasses[size]} ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} data-id="element-8602" />)}
    </div>;
}
// Mode Toggle Component
function ModeToggle({
  currentMode,
  darkMode
}: {
  currentMode: 'edit' | 'preview';
  darkMode: boolean;
}) {
  // This component is no longer used - replaced by banner menu
  return null;
}
// Review Card Component
function ReviewCard({
  review,
  darkMode
}: {
  review: any;
  darkMode: boolean;
}) {
  return <div className={`p-6 rounded-lg ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm border ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-8603">
      <div className="flex items-start justify-between mb-3" data-id="element-8604">
        <div data-id="element-8605">
          <div className="flex items-center gap-2 mb-1" data-id="element-8606">
            <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8607">
              {review.studentName}
            </span>
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8608">
              • {review.instrument}
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-8609">
            <StarRating rating={review.rating} size="sm" data-id="element-8610" />
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8611">
              {new Date(review.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
            </span>
          </div>
        </div>
      </div>

      <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`} data-id="element-8612">
        {review.text}
      </p>

      {review.teacherResponse && <div className={`mt-4 pl-4 border-l-2 ${darkMode ? 'border-deep-pink bg-navy-900/50' : 'border-deep-pink bg-pink-50'} p-3 rounded`} data-id="element-8613">
          <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8614">
            Teacher's Response
          </p>
          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8615">
            {review.teacherResponse}
          </p>
        </div>}
    </div>;
}
// Credential Card Component
function CredentialCard({
  credential,
  darkMode,
  isEditable
}: {
  credential: any;
  darkMode: boolean;
  isEditable: boolean;
}) {
  const iconMap = {
    degree: AwardIcon,
    certificate: CheckCircleIcon,
    award: StarIcon
  };
  const Icon = iconMap[credential.type as keyof typeof iconMap] || AwardIcon;
  return <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm border ${darkMode ? 'border-navy-700' : 'border-gray-200'} ${isEditable ? 'group hover:border-[#10B981] cursor-pointer' : ''} transition-colors relative`} data-id="element-8616">
      {isEditable && <button className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-2 rounded-md bg-navy-700/50 hover:bg-navy-700 transition-opacity" data-id="element-8617">
          <PencilIcon className="h-4 w-4 text-[#10B981]" data-id="element-8618" />
        </button>}
      <div className="flex items-start gap-3" data-id="element-8619">
        <div className={`p-2 rounded-lg ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8620">
          <Icon className={`h-5 w-5 ${darkMode ? 'text-deep-pink' : 'text-deep-pink'}`} data-id="element-8621" />
        </div>
        <div className="flex-1 pr-12" data-id="element-8622">
          <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8623">
            {credential.title}
          </h4>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8624">
            {credential.institution} • {credential.year}
          </p>
          {credential.description && <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8625">
              {credential.description}
            </p>}
          {credential.certificateUrl && <a href={credential.certificateUrl} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 mt-2 text-sm font-medium text-[#10B981] hover:text-[#059669] transition-colors`} data-id="element-8626">
              <FileTextIcon className="h-4 w-4" data-id="element-8627" />
              View Certificate
              <ExternalLinkIcon className="h-3 w-3" data-id="element-8628" />
            </a>}
        </div>
        {isEditable && <div className="absolute top-2 right-2 flex flex-col gap-2" data-id="element-8629">
            <button onClick={() => openEditCredentialModal(credential)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'} transition-colors`} data-id="element-8630">
              <PencilIcon className="h-4 w-4 text-[#10B981]" data-id="element-8631" />
            </button>
          </div>}
      </div>
      {isEditable && <div className="absolute bottom-2 right-2" data-id="element-8632">
          <button onClick={() => setDeleteCredentialId(credential.id)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'} transition-colors`} data-id="element-8633">
            <TrashIcon className="h-4 w-4 text-red-500" data-id="element-8634" />
          </button>
        </div>}
    </div>;
}
// Video Upload Component
function VideoUploadSlot({
  index,
  darkMode,
  isEditable,
  videoUrl,
  videoTitle,
  onUploadComplete,
  onDelete,
  onUpdateTitle
}: {
  index: number;
  darkMode: boolean;
  isEditable: boolean;
  videoUrl?: string;
  videoTitle?: string;
  onUploadComplete?: (url: string) => void;
  onDelete?: () => void;
  onUpdateTitle?: (title: string) => void;
}) {
  const {
    currentUser
  } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Add state for title editing
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(videoTitle || '');
  const titleInputRef = useRef<HTMLInputElement>(null);
  // Add lazy loading state for videos
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  // Update editedTitle when videoTitle prop changes
  useEffect(() => {
    setEditedTitle(videoTitle || '');
  }, [videoTitle]);
  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isEditingTitle]);
  // Intersection Observer for lazy loading videos
  useEffect(() => {
    if (!videoUrl || !videoContainerRef.current) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      });
    }, {
      rootMargin: '50px' // Start loading 50px before video enters viewport
    });
    observer.observe(videoContainerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [videoUrl]);
  const handleFileSelect = async (file: File) => {
    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid video file (MP4, MOV, AVI, or WebM)');
      setTimeout(() => setError(''), 5000);
      return;
    }
    // Validate file size (max 100MB)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Video file size must be less than 100MB');
      setTimeout(() => setError(''), 5000);
      return;
    }
    if (!currentUser?.uid) {
      setError('You must be logged in to upload videos');
      return;
    }
    setIsUploading(true);
    setError('');
    setUploadProgress(0);
    try {
      const {
        uploadFileToStorage
      } = await import('../../utils/fileUploadHelper');
      const downloadURL = await uploadFileToStorage(file, currentUser.uid, {
        storageBasePath: `introVideos/${currentUser.uid}`,
        shouldCompress: false,
        maxFileSize: maxSize,
        allowedTypes,
        onProgress: progress => setUploadProgress(progress),
        onStatusChange: status => setUploadStatus(status),
        customMetadata: {
          videoIndex: index.toString()
        }
      });
      // Call the completion callback with the URL
      if (onUploadComplete) {
        onUploadComplete(downloadURL);
      }
      setUploadStatus('Upload complete!');
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadStatus('');
      }, 2000);
    } catch (err: any) {
      console.error('Video upload error:', err);
      // Enhanced error handling
      let userMessage = 'Failed to upload video. Please try again.';
      if (err.message) {
        if (err.message.includes('network') || err.message.includes('Network')) {
          userMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('timeout') || err.message.includes('timed out')) {
          userMessage = 'Upload timed out. Please try again with a smaller file.';
        } else if (err.message.includes('permission') || err.message.includes('Permission')) {
          userMessage = 'Permission denied. Please contact support.';
        } else if (err.message.includes('storage') || err.message.includes('Storage')) {
          userMessage = 'Storage error. Please try again later.';
        } else {
          userMessage = err.message;
        }
      }
      setError(userMessage);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      setTimeout(() => setError(''), 5000);
    }
  };
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) {
      setIsDragging(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };
  const handleClick = () => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };
  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setShowDeleteConfirm(false);
  };
  const handleSaveTitle = () => {
    if (onUpdateTitle) {
      onUpdateTitle(editedTitle);
    }
    setIsEditingTitle(false);
  };
  const handleCancelTitleEdit = () => {
    setEditedTitle(videoTitle || '');
    setIsEditingTitle(false);
  };
  // If not editable and no video, show empty state
  if (!isEditable && !videoUrl) {
    return <div className="space-y-2" data-id="element-8635">
        <div className={`aspect-video rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-100'} flex items-center justify-center`} data-id="element-8636">
          <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-8637">
            No video uploaded
          </p>
        </div>
      </div>;
  }
  // If video exists, show video player
  if (videoUrl && !isUploading) {
    return <div className="space-y-2" ref={videoContainerRef} data-id="element-8638">
        <div className="aspect-video rounded-lg overflow-hidden bg-black" data-id="element-8639">
          {shouldLoadVideo ? <video src={videoUrl} controls className="w-full h-full" preload="metadata" onError={e => {
          console.error('Video failed to load:', videoUrl);
          // Show error state but don't block page
        }} data-id="element-8640">
              Your browser does not support the video tag.
            </video> : <div className="w-full h-full flex items-center justify-center bg-gray-900" data-id="element-8641">
              <div className="text-center" data-id="element-8642">
                <PlayIcon className="h-12 w-12 text-white mx-auto mb-2" data-id="element-8643" />
                <p className="text-sm text-gray-300" data-id="element-8644">Loading video...</p>
              </div>
            </div>}
        </div>

        {/* Title Section - Edit Mode with Pencil Icon */}
        {isEditable && <div className="overflow-x-hidden" data-id="element-8645">
            {isEditingTitle ?
        // Edit Mode: Input with Save/Cancel
        <div className="space-y-2" data-id="element-8646">
                <input ref={titleInputRef} type="text" value={editedTitle} onChange={e => setEditedTitle(e.target.value)} placeholder="Video title (e.g., 'Introduction')" className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} border focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] max-w-full`} onKeyDown={e => {
            if (e.key === 'Enter') {
              handleSaveTitle();
            } else if (e.key === 'Escape') {
              handleCancelTitleEdit();
            }
          }} data-id="element-8647" />
                <div className="flex gap-2 flex-wrap" data-id="element-8648">
                  <button onClick={handleSaveTitle} className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-transparent border-2 border-[#10B981] text-[#10B981] text-sm font-medium hover:bg-[#10B981]/10 transition-colors whitespace-nowrap" data-id="element-8649">
                    <CheckCircleIcon className="h-4 w-4 flex-shrink-0" data-id="element-8650" />
                    Save
                  </button>
                  <button onClick={handleCancelTitleEdit} className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-transparent border-2 border-red-500 text-red-500 text-sm font-medium hover:bg-red-500/10 transition-colors whitespace-nowrap" data-id="element-8651">
                    <XIcon className="h-4 w-4 flex-shrink-0" data-id="element-8652" />
                    Cancel
                  </button>
                </div>
              </div> :
        // Read-Only Mode: Display with Green Pencil Icon
        <div className="flex items-center justify-between gap-2 group" data-id="element-8653">
                <p className={`text-sm font-medium flex-1 ${darkMode ? 'text-white' : 'text-gray-900'} ${!videoTitle ? darkMode ? 'text-gray-500' : 'text-gray-400' : ''}`} data-id="element-8654">
                  {videoTitle || 'Untitled video'}
                </p>
                <button onClick={() => setIsEditingTitle(true)} className={`p-1.5 rounded-md transition-colors ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} title="Edit title" data-id="element-8655">
                  <PencilIcon className="h-4 w-4 text-[#10B981]" data-id="element-8656" />
                </button>
              </div>}
          </div>}

        {/* Display Title in Preview/Public Mode */}
        {!isEditable && videoTitle && <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8657">
            {videoTitle}
          </p>}

        {isEditable && <div className="flex gap-2" data-id="element-8658">
            <button onClick={handleClick} className={`text-sm ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} transition-colors flex items-center gap-1`} data-id="element-8659">
              <UploadIcon className="h-3.5 w-3.5" data-id="element-8660" />
              Replace video
            </button>
            <button onClick={handleDeleteClick} className="text-sm text-red-500 hover:text-red-600 transition-colors flex items-center gap-1" data-id="element-8661">
              <TrashIcon className="h-3.5 w-3.5" data-id="element-8662" />
              Delete video
            </button>
          </div>}

        <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/x-msvideo,video/webm" onChange={handleFileInputChange} className="hidden" data-id="element-8663" />

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-8664">
            <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-sm w-full`} data-id="element-8665">
              <h3 className={`text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8666">
                Delete Video?
              </h3>
              <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8667">
                Are you sure you want to delete this video? This action cannot
                be undone.
              </p>
              <div className="flex gap-3 justify-end" data-id="element-8668">
                <button onClick={() => setShowDeleteConfirm(false)} className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`} data-id="element-8669">
                  Cancel
                </button>
                <button onClick={handleConfirmDelete} className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white" data-id="element-8670">
                  Delete
                </button>
              </div>
            </div>
          </div>}
      </div>;
  }
  // Editable upload area
  return <div className="space-y-2" data-id="element-8671">
      <div onClick={handleClick} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`aspect-video rounded-lg border-2 border-dashed transition-all ${isUploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-deep-pink'} ${isDragging ? 'border-deep-pink bg-pink-50 dark:bg-pink-900/10' : darkMode ? 'border-navy-600 bg-navy-900/50' : 'border-gray-300 bg-gray-50'} flex flex-col items-center justify-center`} data-id="element-8672">
        {isUploading ? <div className="flex flex-col items-center gap-3" data-id="element-8673">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" data-id="element-8674" />
            <div className="text-center" data-id="element-8675">
              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8676">
                {uploadStatus || 'Uploading...'}
              </p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8677">
                {uploadProgress}%
              </p>
            </div>
            <div className="w-48 h-2 bg-gray-200 dark:bg-navy-700 rounded-full overflow-hidden" data-id="element-8678">
              <div className="h-full bg-blue-500 transition-all duration-300" style={{
            width: `${uploadProgress}%`
          }} data-id="element-8679" />
            </div>
          </div> : <>
            <UploadIcon className={`h-10 w-10 mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-8680" />
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} text-center px-4`} data-id="element-8681">
              {isDragging ? 'Drop video here' : 'Click to upload or drag & drop'}
            </p>
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'} mt-1`} data-id="element-8682">
              MP4, WebM up to 100MB
            </p>
          </>}
      </div>

      {error && <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20" data-id="element-8683">
          <p className="text-sm text-red-500" data-id="element-8684">{error}</p>
        </div>}

      <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/x-msvideo,video/webm" onChange={handleFileInputChange} disabled={isUploading} className="hidden" data-id="element-8685" />
    </div>;
}
// Editable Text Field Component
function EditableTextField({
  value,
  isEditable,
  multiline = false,
  placeholder = 'Click to edit',
  darkMode
}: {
  value: string;
  isEditable: boolean;
  multiline?: boolean;
  placeholder?: string;
  darkMode: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  if (!isEditable) {
    return multiline ? <div className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed whitespace-pre-wrap`} data-id="element-8686">
        {value}
      </div> : <span data-id="element-8687">{value}</span>;
  }
  if (isEditing) {
    return <div className="space-y-2" data-id="element-8688">
        {multiline ? <textarea value={editValue} onChange={e => setEditValue(e.target.value)} className={`w-full px-3 py-2 rounded-md text-sm min-h-[150px] ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink focus:border-deep-pink`} autoFocus data-id="element-8689" /> : <input type="text" value={editValue} onChange={e => setEditValue(e.target.value)} className={`w-full px-3 py-2 rounded-md text-sm ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink focus:border-deep-pink`} autoFocus data-id="element-8690" />}
        <div className="flex gap-2" data-id="element-8691">
          <button onClick={() => {
          // Save logic here
          setIsEditing(false);
        }} className="px-4 py-2 bg-deep-pink text-white rounded-lg text-sm font-medium hover:bg-pink-600 transition-colors" data-id="element-8692">
            Save
          </button>
          <button onClick={() => {
          setEditValue(value);
          setIsEditing(false);
        }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`} data-id="element-8693">
            Cancel
          </button>
        </div>
      </div>;
  }
  return <div onClick={() => setIsEditing(true)} className={`group cursor-pointer relative ${multiline ? 'leading-relaxed whitespace-pre-wrap' : ''} ${darkMode ? 'text-gray-300' : 'text-gray-700'} hover:bg-opacity-50 ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} p-2 -m-2 rounded transition-colors`} data-id="element-8694">
      {value || <span className={darkMode ? 'text-gray-500' : 'text-gray-400'} data-id="element-8695">
          {placeholder}
        </span>}
      <PencilIcon className="h-4 w-4 absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-deep-pink transition-opacity" data-id="element-8696" />
    </div>;
}
// Profile Content Component
function ProfileContent({
  mode,
  teacherId,
  darkMode
}: {
  mode: 'edit' | 'preview' | 'public';
  teacherId: string;
  darkMode: boolean;
}) {
  const {
    currentUser
  } = useAuth();
  const isEditable = mode === 'edit';
  const isPublicView = mode === 'public';
  // Feature flag: Hide Student Reviews for teacher-only MVP
  // Will re-enable when student marketplace launches with real review data
  const SHOW_REVIEWS = false;
  // Banner menu state
  const [showBannerMenu, setShowBannerMenu] = useState(false);
  const bannerMenuRef = useRef<HTMLDivElement>(null);
  // Add share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  // Add copy toast state
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [copyToastMessage, setCopyToastMessage] = useState('');
  // Ref to track toast dismiss timeout (prevents stacking)
  const copyToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // =========================================================================
  // DATA LOADING: Use public hook for shared links, private hook for dashboard
  // =========================================================================
  // Private hook (for teacher's own dashboard — reads from teachers/{teacherId})
  const privateProfile = useTeacherProfile(isPublicView ? undefined : teacherId);
  // Public hook (for shared links — reads from publicTeacherProfiles/{teacherId})
  const publicProfile = usePublicTeacherProfile(isPublicView ? teacherId : undefined);
  // Select the correct data source based on mode
  const activeProfile = isPublicView ? publicProfile : privateProfile;
  const {
    profile,
    loading,
    error
  } = activeProfile;
  // Only available in private mode
  const updateProfile = isPublicView ? async () => {} : privateProfile.updateProfile;
  const uploadBanner = isPublicView ? async () => '' : privateProfile.uploadBanner;
  // Auto-sync: When teacher views their own profile page (edit mode),
  // trigger a full sync to ensure publicTeacherProfiles is populated
  const hasSyncedRef = useRef(false);
  useEffect(() => {
    if (!isPublicView && currentUser?.uid && teacherId && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      // Non-blocking full sync to populate/refresh the public profile
      fullSyncPublicProfile(teacherId).catch(err => {
        console.warn('[TeacherPublicProfilePage] Auto-sync failed (non-blocking):', err);
      });
    }
  }, [isPublicView, currentUser?.uid, teacherId]);
  // Use profile data with fallback to mock data structure
  const teacher = profile ? {
    ...mockTeacherData,
    ...profile,
    // Map profileImageUrl to profilePhotoUrl for consistency with mock data
    profilePhotoUrl: profile.profileImageUrl || profile.profilePhotoUrl || mockTeacherData.profilePhotoUrl,
    // Ensure required fields have defaults
    instruments: profile.instruments || [],
    teachingLevels: profile.teachingLevels || mockTeacherData.teachingLevels,
    lessonFormats: profile.lessonFormats || [],
    languages: profile.languages || [],
    pricing: profile.pricing || mockTeacherData.pricing,
    credentials: profile.credentials || [],
    reviews: profile.reviews || mockTeacherData.reviews,
    availableSlots: profile.availableSlots || mockTeacherData.availableSlots,
    introVideos: profile.introVideos || []
  } : mockTeacherData;
  // Modal state for section editing
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<'about' | 'philosophy' | 'quickInfo' | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Add Credential modal state
  const [addCredentialModalOpen, setAddCredentialModalOpen] = useState(false);
  const [newCredential, setNewCredential] = useState({
    type: 'degree' as 'degree' | 'certificate' | 'award',
    title: '',
    institution: '',
    year: '',
    description: '',
    certificateUrl: ''
  });
  const [newCredentialFile, setNewCredentialFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Add year validation state
  const [yearError, setYearError] = useState<string | null>(null);
  // Edit Credential modal state
  const [editCredentialModalOpen, setEditCredentialModalOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState<any>(null);
  const [editCredentialFile, setEditCredentialFile] = useState<File | null>(null);
  // Delete Credential confirmation state
  const [deleteCredentialId, setDeleteCredentialId] = useState<string | null>(null);
  // Banner image edit state
  const [editBannerModalOpen, setEditBannerModalOpen] = useState(false);
  const [newBannerImage, setNewBannerImage] = useState<File | null>(null);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const bannerFileInputRef = useRef<HTMLInputElement>(null);
  // Quick Info state
  const [quickInfoData, setQuickInfoData] = useState({
    instruments: teacher.instruments || [],
    teachingLevels: teacher.teachingLevels || {},
    lessonFormats: teacher.lessonFormats || [],
    languages: teacher.languages || [],
    pricing: teacher.pricing || {
      isPublic: true,
      startingPrice: 0,
      contactForPricing: false
    }
  });
  // Update quickInfoData when profile changes - BUT NOT WHILE EDITING
  useEffect(() => {
    if (teacher && !editModalOpen) {
      setQuickInfoData({
        instruments: teacher.instruments || [],
        teachingLevels: teacher.teachingLevels || {},
        lessonFormats: teacher.lessonFormats || [],
        languages: teacher.languages || [],
        pricing: teacher.pricing || {
          isPublic: true,
          startingPrice: 0,
          contactForPricing: false
        }
      });
    }
  }, [teacher, editModalOpen]);
  // Close banner menu when clicking outside
  useEffect(() => {
    if (!showBannerMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (bannerMenuRef.current && !bannerMenuRef.current.contains(e.target as Node)) {
        setShowBannerMenu(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowBannerMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showBannerMenu]);
  // Show loading state
  if (loading) {
    return <div className={`min-h-screen ${darkMode ? 'bg-navy-900' : 'bg-gray-50'}`} data-id="element-8697">
        {/* Skeleton Loading State - Progressive Render */}
        <div className="animate-pulse" data-id="element-8698">
          {/* Hero Section Skeleton */}
          <div className={`relative h-48 md:h-56 rounded-xl overflow-hidden ${darkMode ? 'bg-navy-800' : 'bg-gray-200'}`} data-id="element-8699">
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/40" data-id="element-8700" />
            <div className="relative h-full px-6" data-id="element-8701">
              <div className="flex flex-col justify-end h-full pb-6" data-id="element-8702">
                <div className="flex flex-col md:flex-row md:items-end gap-4" data-id="element-8703">
                  {/* Profile Photo Skeleton */}
                  <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full ${darkMode ? 'bg-navy-700' : 'bg-gray-300'}`} data-id="element-8704" />
                  {/* Name Skeleton */}
                  <div className="flex-1" data-id="element-8705">
                    <div className={`h-8 w-48 rounded mb-2 ${darkMode ? 'bg-navy-700' : 'bg-gray-300'}`} data-id="element-8706" />
                    <div className={`h-4 w-32 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-300'}`} data-id="element-8707" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Skeleton */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-8708">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-8709">
              {/* Main Column */}
              <div className="lg:col-span-2 space-y-8" data-id="element-8710">
                {/* About Section Skeleton */}
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-8711">
                  <div className={`h-6 w-32 rounded mb-4 ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8712" />
                  <div className="space-y-2" data-id="element-8713">
                    <div className={`h-4 w-full rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8714" />
                    <div className={`h-4 w-full rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8715" />
                    <div className={`h-4 w-3/4 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8716" />
                  </div>
                </div>
              </div>

              {/* Sidebar Skeleton */}
              <div data-id="element-8717">
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-8718">
                  <div className={`h-6 w-32 rounded mb-4 ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8719" />
                  <div className="space-y-3" data-id="element-8720">
                    <div className={`h-4 w-full rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8721" />
                    <div className={`h-4 w-full rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8722" />
                    <div className={`h-4 w-2/3 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8723" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Show error state
  if (error) {
    return <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-navy-900' : 'bg-gray-50'}`} data-id="element-8724">
        <div className="text-center" data-id="element-8725">
          <p className={`text-red-500 mb-4`} data-id="element-8726">
            Error loading profile: {error.message}
          </p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-[#10B981] text-white rounded-lg hover:bg-[#059669]" data-id="element-8727">
            Retry
          </button>
        </div>
      </div>;
  }
  // Show not found state
  if (!teacher && !loading) {
    return <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-navy-900' : 'bg-gray-50'}`} data-id="element-8728">
        <div className="text-center" data-id="element-8729">
          <p className={darkMode ? 'text-gray-300' : 'text-gray-700'} data-id="element-8730">
            Profile not found
          </p>
        </div>
      </div>;
  }
  const openEditModal = (section: 'about' | 'philosophy' | 'quickInfo') => {
    setEditingSection(section);
    // Pre-fill with current content
    if (section === 'about') {
      setEditValue(teacher.bio);
    } else if (section === 'philosophy') {
      setEditValue(teacher.teachingPhilosophy);
    } else if (section === 'quickInfo') {
      // Create a deep copy to avoid reference issues
      const quickInfoSnapshot = {
        instruments: [...(teacher.instruments || [])],
        teachingLevels: {
          ...(teacher.teachingLevels || {})
        },
        lessonFormats: [...(teacher.lessonFormats || [])],
        languages: (teacher.languages || []).map(lang => ({
          language: lang.language || '',
          proficiency: lang.proficiency || 'Conversational'
        })),
        pricing: {
          isPublic: teacher.pricing?.isPublic ?? true,
          startingPrice: teacher.pricing?.startingPrice ?? 0,
          contactForPricing: teacher.pricing?.contactForPricing ?? false,
          currency: teacher.pricing?.currency || 'USD'
        }
      };
      console.log('Opening Quick Info modal with data:', quickInfoSnapshot);
      setQuickInfoData(quickInfoSnapshot);
    }
    setEditModalOpen(true);
  };
  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingSection(null);
    setEditValue('');
    setIsSaving(false);
  };
  const handleSaveSection = async () => {
    if (!currentUser?.uid) return;
    setIsSaving(true);
    try {
      // Determine which field to update
      const updates: any = {};
      if (editingSection === 'about') {
        updates.bio = editValue;
      } else if (editingSection === 'philosophy') {
        updates.teachingPhilosophy = editValue;
      } else if (editingSection === 'quickInfo') {
        console.log('[Quick Info] Saving changes:', quickInfoData);
        updates.instruments = quickInfoData.instruments;
        updates.teachingLevels = quickInfoData.teachingLevels;
        updates.lessonFormats = quickInfoData.lessonFormats;
        updates.languages = quickInfoData.languages;
        updates.pricing = quickInfoData.pricing;
        console.log('[Quick Info] Updates to save:', updates);
      }
      // Save to Firestore using the hook
      await updateProfile(updates);
      console.log('[Quick Info] Save successful');
      closeEditModal();
    } catch (error) {
      console.error('[Quick Info] Save failed:', error);
      alert(`Failed to save changes: ${(error as Error).message || 'Unknown error'}`);
      setIsSaving(false);
    }
  };
  // Helper functions for Quick Info editing
  const toggleInstrument = (instrument: string) => {
    console.log('toggleInstrument called:', instrument);
    setQuickInfoData(prev => ({
      ...prev,
      instruments: prev.instruments.includes(instrument) ? prev.instruments.filter(i => i !== instrument) : [...prev.instruments, instrument]
    }));
  };
  const addInstrument = (instrument: string) => {
    console.log('addInstrument called:', instrument);
    if (instrument.trim() && !quickInfoData.instruments.includes(instrument.trim())) {
      setQuickInfoData(prev => ({
        ...prev,
        instruments: [...prev.instruments, instrument.trim()]
      }));
    }
  };
  const removeInstrument = (instrument: string) => {
    console.log('removeInstrument called:', instrument);
    setQuickInfoData(prev => ({
      ...prev,
      instruments: (prev?.instruments || []).filter(i => i !== instrument)
    }));
  };
  const toggleTeachingLevel = (level: 'beginner' | 'intermediate' | 'advanced') => {
    console.log('toggleTeachingLevel called:', level, 'current:', quickInfoData.teachingLevels[level]);
    setQuickInfoData(prev => {
      const newData = {
        ...prev,
        teachingLevels: {
          ...(prev?.teachingLevels || {}),
          [level]: !(prev?.teachingLevels?.[level] ?? false)
        }
      };
      console.log('New quickInfoData after toggle:', newData);
      return newData;
    });
  };
  const toggleLessonFormat = (format: string) => {
    console.log('toggleLessonFormat called:', format);
    setQuickInfoData(prev => ({
      ...prev,
      lessonFormats: (prev?.lessonFormats || []).includes(format) ? (prev?.lessonFormats || []).filter(f => f !== format) : [...(prev?.lessonFormats || []), format]
    }));
  };
  const updateLanguage = (index: number, field: 'language' | 'proficiency', value: string) => {
    console.log('updateLanguage called:', index, field, value);
    setQuickInfoData(prev => ({
      ...prev,
      languages: (prev?.languages || []).map((lang, i) => i === index ? {
        ...lang,
        [field]: value
      } : lang)
    }));
  };
  const addLanguage = () => {
    console.log('addLanguage called');
    setQuickInfoData(prev => ({
      ...prev,
      languages: [...(prev?.languages || []), {
        language: '',
        proficiency: 'Conversational'
      }]
    }));
  };
  const removeLanguage = (index: number) => {
    console.log('removeLanguage called:', index);
    setQuickInfoData(prev => ({
      ...prev,
      languages: (prev?.languages || []).filter((_, i) => i !== index)
    }));
  };
  const updatePricing = (field: keyof typeof quickInfoData.pricing, value: any) => {
    console.log('updatePricing called:', field, value);
    setQuickInfoData(prev => ({
      ...prev,
      pricing: {
        ...(prev?.pricing || {
          isPublic: true,
          startingPrice: 0,
          contactForPricing: false
        }),
        [field]: value
      }
    }));
  };
  const uploadCertificateFile = async (file: File, credentialId: string): Promise<string> => {
    if (!currentUser?.uid) throw new Error('User not authenticated');
    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload PDF, JPG, or PNG files only.');
    }
    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('File size exceeds 10MB limit.');
    }
    // Create storage reference
    const timestamp = Date.now();
    const fileName = `${credentialId}_${timestamp}_${file.name}`;
    const storageRef = ref(storage, `certificates/${currentUser.uid}/${credentialId}/${fileName}`);
    // Upload file
    await uploadBytes(storageRef, file);
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  };
  const openAddCredentialModal = () => {
    setNewCredential({
      type: 'degree',
      title: '',
      institution: '',
      year: '',
      description: '',
      certificateUrl: ''
    });
    setNewCredentialFile(null);
    setUploadProgress(0);
    setUploadStatus('');
    setYearError(null);
    setAddCredentialModalOpen(true);
  };
  const closeAddCredentialModal = () => {
    setAddCredentialModalOpen(false);
    setNewCredential({
      type: 'degree',
      title: '',
      institution: '',
      year: '',
      description: '',
      certificateUrl: ''
    });
    setNewCredentialFile(null);
    setUploadProgress(0);
    setUploadStatus('');
    setIsSaving(false);
    setYearError(null);
  };
  // Year validation helper
  const validateYear = (year: string, type: 'degree' | 'certificate' | 'award'): string | null => {
    // For awards, year is optional
    if (type === 'award' && year.trim() === '') {
      return null;
    }
    // For degrees and certificates, year is required
    if ((type === 'degree' || type === 'certificate') && year.trim() === '') {
      return 'Year Awarded is required for degrees and certificates';
    }
    // If year is provided, validate format and range
    if (year.trim() !== '') {
      // Check if it's exactly 4 digits
      if (!/^\d{4}$/.test(year)) {
        return 'Please enter a valid year (4 digits, e.g. 2005)';
      }
      const yearNum = parseInt(year, 10);
      const currentYear = new Date().getFullYear();
      // Check range: 1950 to current year
      if (yearNum < 1950 || yearNum > currentYear) {
        return `Year must be between 1950 and ${currentYear}`;
      }
    }
    return null;
  };
  const handleYearChange = (value: string) => {
    setNewCredential(prev => ({
      ...prev,
      year: value
    }));
    // Validate on change
    const error = validateYear(value, newCredential.type);
    setYearError(error);
  };
  const handleCredentialTypeChange = (type: 'degree' | 'certificate' | 'award') => {
    setNewCredential(prev => ({
      ...prev,
      type: type
    }));
    // Re-validate year with new type
    const error = validateYear(newCredential.year, type);
    setYearError(error);
  };
  const handleSaveCredential = async () => {
    if (!currentUser?.uid) return;
    // Validate year before saving
    const yearValidationError = validateYear(newCredential.year, newCredential.type);
    if (yearValidationError) {
      setYearError(yearValidationError);
      return;
    }
    if (!newCredential.title || !newCredential.institution) {
      alert('Please fill in all required fields (Title, Institution, Year Awarded)');
      return;
    }
    setIsSaving(true);
    try {
      const credentialId = Date.now().toString();
      let certificateUrl = '';
      // Upload certificate file if provided
      if (newCredentialFile) {
        setUploadProgress(10);
        certificateUrl = await uploadCertificateFile(newCredentialFile, credentialId);
        setUploadProgress(100);
      }
      // Build credential object WITHOUT undefined values
      const newCredentialObj: any = {
        id: credentialId,
        type: newCredential.type,
        title: newCredential.title,
        institution: newCredential.institution,
        year: newCredential.year,
        description: newCredential.description || ''
      };
      // Only add certificateUrl if it exists
      if (certificateUrl) {
        newCredentialObj.certificateUrl = certificateUrl;
      }
      // Add new credential to existing credentials array
      const updatedCredentials = [...(teacher.credentials || []), newCredentialObj];
      // Save to Firestore using the hook
      await updateProfile({
        credentials: updatedCredentials
      });
      closeAddCredentialModal();
    } catch (error: any) {
      console.error('Failed to save credential:', error);
      alert(error.message || 'Failed to save credential. Please try again.');
      setIsSaving(false);
    }
  };
  const openEditCredentialModal = (credential: any) => {
    setEditingCredential(credential);
    setEditCredentialFile(null);
    setUploadProgress(0);
    setEditCredentialModalOpen(true);
  };
  const closeEditCredentialModal = () => {
    setEditCredentialModalOpen(false);
    setEditingCredential(null);
    setEditCredentialFile(null);
    setUploadProgress(0);
    setUploadStatus('');
    setIsSaving(false);
  };
  const handleUpdateCredential = async () => {
    if (!currentUser?.uid || !editingCredential) return;
    if (!editingCredential.title || !editingCredential.institution || !editingCredential.year) {
      alert('Please fill in all required fields (Title, Institution, Year)');
      return;
    }
    setIsSaving(true);
    try {
      let certificateUrl = editingCredential.certificateUrl || '';
      // Upload new certificate file if provided
      if (editCredentialFile) {
        setUploadProgress(10);
        // Delete old certificate if exists
        if (editingCredential.certificateUrl) {
          try {
            const oldRef = ref(storage, editingCredential.certificateUrl);
            await deleteObject(oldRef);
          } catch (error) {
            console.warn('Could not delete old certificate:', error);
          }
        }
        certificateUrl = await uploadCertificateFile(editCredentialFile, editingCredential.id);
        setUploadProgress(100);
      }
      // Build updated credential object WITHOUT undefined values
      const updatedCredentialObj: any = {
        id: editingCredential.id,
        type: editingCredential.type,
        title: editingCredential.title,
        institution: editingCredential.institution,
        year: editingCredential.year,
        description: editingCredential.description || ''
      };
      // Only add certificateUrl if it exists
      if (certificateUrl) {
        updatedCredentialObj.certificateUrl = certificateUrl;
      }
      // Update credential in array
      const updatedCredentials = (teacher.credentials || []).map(cred => cred.id === editingCredential.id ? updatedCredentialObj : cred);
      // Save to Firestore using the hook
      await updateProfile({
        credentials: updatedCredentials
      });
      closeEditCredentialModal();
    } catch (error: any) {
      console.error('Failed to update credential:', error);
      alert(error.message || 'Failed to update credential. Please try again.');
      setIsSaving(false);
    }
  };
  const handleDeleteCredential = async (credentialId: string) => {
    if (!currentUser?.uid) return;
    setIsSaving(true);
    try {
      // Find the credential to get certificate URL
      const credentialToDelete = (teacher.credentials || []).find(cred => cred.id === credentialId);
      // Delete certificate file from storage if exists
      if (credentialToDelete?.certificateUrl) {
        try {
          const certRef = ref(storage, credentialToDelete.certificateUrl);
          await deleteObject(certRef);
        } catch (error) {
          console.warn('Could not delete certificate file:', error);
        }
      }
      // Remove credential from array
      const updatedCredentials = (teacher.credentials || []).filter(cred => cred.id !== credentialId);
      // Save to Firestore using the hook
      await updateProfile({
        credentials: updatedCredentials
      });
      setDeleteCredentialId(null);
      setIsSaving(false);
    } catch (error) {
      console.error('Failed to delete credential:', error);
      alert('Failed to delete credential. Please try again.');
      setIsSaving(false);
    }
  };
  const openEditBannerModal = () => {
    setBannerPreviewUrl(teacher.bannerImageUrl);
    setEditBannerModalOpen(true);
  };
  const closeEditBannerModal = () => {
    setEditBannerModalOpen(false);
    setNewBannerImage(null);
    setBannerPreviewUrl(null);
    setIsSaving(false);
    if (bannerFileInputRef.current) {
      bannerFileInputRef.current.value = '';
    }
  };
  const handleBannerFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      // Validate file type
      if (!selectedFile.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      // Validate file size (max 5MB for banner)
      if (selectedFile.size > 5 * 1024 * 1024) {
        alert('File size exceeds 5MB limit');
        return;
      }
      setNewBannerImage(selectedFile);
      // Create preview URL
      const reader = new FileReader();
      reader.onload = e => {
        setBannerPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };
  const handleSaveBannerImage = async () => {
    if (!currentUser?.uid || !newBannerImage) return;
    setIsSaving(true);
    try {
      // Upload banner using the hook (which handles both upload and Firestore update)
      await uploadBanner(newBannerImage, progress => {
        console.log('Upload progress:', progress);
      });
      closeEditBannerModal();
    } catch (error) {
      console.error('Failed to upload banner image:', error);
      alert('Failed to upload banner image. Please try again.');
      setIsSaving(false);
    }
  };
  const handleViewAsStudent = () => {
    // Open public view in new tab
    window.open(`/teachers/${currentUser?.uid}`, '_blank');
  };
  const handleShareProfile = () => {
    setShowBannerMenu(false);
    setShowShareModal(true);
  };
  const handleCopyLink = () => {
    const publicUrl = `${window.location.origin}/teachers/${currentUser?.uid}`;
    navigator.clipboard.writeText(publicUrl);
    // Clear any existing timeout to prevent stacking
    if (copyToastTimeoutRef.current) {
      clearTimeout(copyToastTimeoutRef.current);
    }
    setCopyToastMessage('Link copied to clipboard!');
    setShowCopyToast(true);
    copyToastTimeoutRef.current = setTimeout(() => {
      setShowCopyToast(false);
      copyToastTimeoutRef.current = null;
    }, 3500);
    setShowShareModal(false);
  };
  const handleCopyEmailText = () => {
    const publicUrl = `${window.location.origin}/teachers/${currentUser?.uid}`;
    const emailText = `Subject: Teacher Profile\n\nCheck out my teaching profile:\n\n${publicUrl}`;
    navigator.clipboard.writeText(emailText);
    // Clear any existing timeout to prevent stacking
    if (copyToastTimeoutRef.current) {
      clearTimeout(copyToastTimeoutRef.current);
    }
    setCopyToastMessage('Email text copied! Paste into your email client.');
    setShowCopyToast(true);
    copyToastTimeoutRef.current = setTimeout(() => {
      setShowCopyToast(false);
      copyToastTimeoutRef.current = null;
    }, 3500);
    setShowShareModal(false);
  };
  const handleEmailLink = () => {
    const publicUrl = `${window.location.origin}/teachers/${currentUser?.uid}`;
    const subject = 'Teacher Profile';
    const body = `Check out my teaching profile:\n\n${publicUrl}`;
    // Try Web Share API first (works on mobile and some desktop browsers)
    if (navigator.share) {
      navigator.share({
        title: subject,
        text: body,
        url: publicUrl
      }).then(() => {
        setShowShareModal(false);
      }).catch(error => {
        // User cancelled or share failed - fall back to Gmail web compose
        if (error.name !== 'AbortError') {
          openGmailCompose(publicUrl, subject, body);
        }
      });
    } else {
      // No Web Share API - use Gmail web compose (safe, no OAuth errors)
      openGmailCompose(publicUrl, subject, body);
    }
  };
  const openGmailCompose = (publicUrl: string, subject: string, body: string) => {
    // Gmail web compose URL - opens in browser, no OAuth/account setup required
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
    setShowShareModal(false);
  };
  return <div className={`${isPublicView ? 'min-h-screen' : ''} overflow-x-hidden max-w-full`} data-id="element-8731">
      {/* Mode Toggle - Only show in edit/preview modes, not in public view */}
      {!isPublicView && <ModeToggle currentMode={mode as 'edit' | 'preview'} darkMode={darkMode} data-id="element-8732" />}

      {/* Hero Section */}
      <div className={`relative ${isPublicView ? 'h-36 md:h-48' : 'h-32 md:h-48 mb-6'} rounded-xl overflow-hidden`} data-id="element-8733">
        {/* Banner Image */}
        <div className="absolute inset-0" data-id="element-8734">
          <img src={teacher.bannerImageUrl} alt="Banner" className="w-full h-full object-cover" loading="lazy" onError={e => {
          // Fallback to placeholder if image fails to load
          e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1200" height="400"%3E%3Crect fill="%23374151" width="1200" height="400"/%3E%3C/svg%3E';
        }} data-id="element-8735" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" data-id="element-8736" />
        </div>

        {/* Banner Menu Button - Only show in edit/preview modes */}
        {!isPublicView && <div className="absolute top-4 right-4 z-10" ref={bannerMenuRef} data-id="element-8737">
            <button onClick={() => setShowBannerMenu(!showBannerMenu)} className="p-2 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm transition-colors" aria-label="More options" data-id="element-8738">
              <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-8739">
                <circle cx="12" cy="5" r="1.5" fill="currentColor" data-id="element-8740" />
                <circle cx="12" cy="12" r="1.5" fill="currentColor" data-id="element-8741" />
                <circle cx="12" cy="19" r="1.5" fill="currentColor" data-id="element-8742" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showBannerMenu && <div className={`absolute top-full right-0 mt-2 w-64 rounded-xl shadow-lg overflow-hidden ${darkMode ? 'bg-[#1e1e1e]' : 'bg-white'}`} data-id="element-8743">
                <button onClick={handleViewAsStudent} className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${darkMode ? 'hover:bg-[#2a2a2a] text-white' : 'hover:bg-gray-50 text-gray-900'}`} data-id="element-8744">
                  <EyeIcon className="h-5 w-5" data-id="element-8745" />
                  <span className="text-sm font-medium" data-id="element-8746">View as Student</span>
                </button>
                <button onClick={handleShareProfile} className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${darkMode ? 'hover:bg-[#2a2a2a] text-white' : 'hover:bg-gray-50 text-gray-900'}`} data-id="element-8747">
                  <Share2Icon className="h-5 w-5" data-id="element-8748" />
                  <span className="text-sm font-medium" data-id="element-8749">
                    Share Teacher Page
                  </span>
                </button>
              </div>}
          </div>}

        {/* Hero Content */}
        <div className="relative h-full px-6" data-id="element-8750">
          <div className="flex flex-col justify-end md:justify-center h-full pb-2 md:pb-0" data-id="element-8751">
            {/* MOBILE: Horizontal row layout, DESKTOP: Horizontal row layout */}
            <div className="flex flex-row items-center md:flex-row md:items-center gap-4" data-id="element-8752">
              {/* Profile Photo */}
              <div className="relative flex-shrink-0" data-id="element-8753">
                <img src={teacher.profilePhotoUrl} alt={`${teacher.firstName} ${teacher.lastName}`} className="w-20 h-20 md:w-32 md:h-32 rounded-full border-4 border-white shadow-xl object-cover" loading="lazy" onError={e => {
                // Fallback to placeholder if image fails to load
                e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="128" height="128"%3E%3Ccircle fill="%239CA3AF" cx="64" cy="64" r="64"/%3E%3C/svg%3E';
              }} data-id="element-8754" />
              </div>

              {/* Name and Info */}
              <div className="flex-1 text-white min-w-0 md:mt-12" data-id="element-8755">
                <h1 className="text-xl md:text-3xl font-bold mb-1 truncate" data-id="element-8756">
                  {teacher.firstName} {teacher.lastName}
                </h1>
                <div className="flex items-center gap-1 mb-1" data-id="element-8757">
                  <MapPinIcon className="h-4 w-4 flex-shrink-0" data-id="element-8758" />
                  <span className="text-sm truncate" data-id="element-8759">
                    {teacher.city}, {teacher.state}
                  </span>
                </div>
              </div>

              {/* CTA Buttons - Only show in public view - HIDDEN FOR LAUNCH */}
              {false && isPublicView && <div className="hidden md:flex flex-col gap-3" data-id="element-8760">
                  <button className="bg-deep-pink hover:bg-pink-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg flex items-center justify-center gap-2" data-id="element-8761">
                    <CalendarIcon className="h-5 w-5" data-id="element-8762" />
                    Book a Lesson
                  </button>
                  <button className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2" data-id="element-8763">
                    <MessageSquareIcon className="h-5 w-5" data-id="element-8764" />
                    Send Message
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Quick Info Card - Show on small screens in all modes */}
      <div className="lg:hidden mb-6" data-id="element-8765">
        <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-8766">
          <div className="flex items-center justify-between mb-4" data-id="element-8767">
            <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8768">
              Quick Info
            </h3>
            {isEditable && <button onClick={() => openEditModal('quickInfo')} className="p-2 rounded-md bg-navy-700/50 hover:bg-navy-700 transition-colors" data-id="element-8769">
                <PencilIcon className="h-4 w-4 text-[#10B981]" data-id="element-8770" />
              </button>}
          </div>

          <div className="space-y-4" data-id="element-8771">
            {/* Instruments */}
            <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8772">
              <p className={`text-base font-bold mb-3 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`} data-id="element-8773">
                Instruments Taught
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-8774">
                {teacher.instruments.map(instrument => <span key={instrument} className={`px-3 py-1 rounded-full text-base font-normal ${darkMode ? 'bg-navy-700/50 text-gray-200' : 'bg-gray-100 text-gray-900'}`} data-id="element-8775">
                    {instrument}
                  </span>)}
              </div>
            </div>

            {/* Teaching Levels */}
            <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8776">
              <p className={`text-base font-bold mb-3 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`} data-id="element-8777">
                Teaching Levels
              </p>
              <div className="space-y-2" data-id="element-8778">
                {Object.entries(teacher.teachingLevels).map(([level, teaches]) => teaches && <div key={level} className="flex items-center gap-2" data-id="element-8779">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" data-id="element-8780" />
                        <span className={`text-base font-normal capitalize ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8781">
                          {level}
                        </span>
                      </div>)}
              </div>
            </div>

            {/* Lesson Formats */}
            <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8782">
              <p className={`text-base font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8783">
                Lesson Formats
              </p>
              <div className="space-y-2" data-id="element-8784">
                {teacher.lessonFormats.map(format => <div key={format} className="flex items-center gap-2" data-id="element-8785">
                    <span className={`text-base font-normal ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8786">
                      {format}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Pricing */}
            {teacher.pricing.isPublic && <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8787">
                <p className={`text-base font-bold mb-3 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`} data-id="element-8788">
                  Pricing
                </p>
                {teacher.pricing.contactForPricing ? <p className={`text-base font-normal ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8789">
                    Contact for pricing
                  </p> : <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8790">
                    ${teacher.pricing.startingPrice}
                    <span className={`text-base font-normal ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8791">
                      /hour
                    </span>
                  </p>}
              </div>}

            {/* Languages */}
            <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8792">
              <p className={`text-base font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8793">
                Languages Spoken
              </p>
              <div className="space-y-2" data-id="element-8794">
                {teacher.languages.map(lang => <div key={lang.language} className="flex items-center gap-2" data-id="element-8795">
                    <GlobeIcon className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8796" />
                    <span className={`text-base font-normal ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8797">
                      {lang.language}
                    </span>
                    <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8798">
                      ({lang.proficiency})
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Response Time */}
            <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8799">
              <p className={`text-base font-bold mb-3 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`} data-id="element-8800">
                Response Time
              </p>
              <div className="flex items-center gap-2" data-id="element-8801">
                <ClockIcon className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8802" />
                <span className={`text-base font-normal ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8803">
                  Usually responds within 2 hours
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`${isPublicView ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8' : ''} overflow-x-hidden`} data-id="element-8804">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-8805">
          {/* Main Column (2/3) */}
          <div className="lg:col-span-2 space-y-8" data-id="element-8806">
            {/* About Me */}
            <section className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-8807">
              <div className="flex items-center justify-between mb-4" data-id="element-8808">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8809">
                  About Me
                </h2>
                {isEditable && <button onClick={() => openEditModal('about')} className="p-2 rounded-md bg-navy-700/50 hover:bg-navy-700 transition-colors" data-id="element-8810">
                    <PencilIcon className="h-4 w-4 text-[#10B981]" data-id="element-8811" />
                  </button>}
              </div>
              <div className={`space-y-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`} data-id="element-8812">
                {teacher.bio.split('\n\n').map((paragraph, index) => <p key={index} data-id="element-8813">{paragraph}</p>)}
              </div>
            </section>

            {/* Teaching Philosophy */}
            <section className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-8814">
              <div className="flex items-center justify-between mb-4" data-id="element-8815">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8816">
                  Teaching Philosophy
                </h2>
                {isEditable && <button onClick={() => openEditModal('philosophy')} className="p-2 rounded-md bg-navy-700/50 hover:bg-navy-700 transition-colors" data-id="element-8817">
                    <PencilIcon className="h-4 w-4 text-[#10B981]" data-id="element-8818" />
                  </button>}
              </div>
              <div className={`space-y-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`} data-id="element-8819">
                {teacher.teachingPhilosophy.split('\n\n').map((paragraph, index) => <p key={index} data-id="element-8820">{paragraph}</p>)}
              </div>
            </section>

            {/* Introduction Videos */}
            {(isEditable || teacher.introVideos && teacher.introVideos.length > 0 && teacher.introVideos.some(url => url)) && <section className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-8821">
                <div className="flex items-center justify-between mb-4" data-id="element-8822">
                  <div className="flex items-center gap-2" data-id="element-8823">
                    <VideoIcon className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} data-id="element-8824" />
                    <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8825">
                      Introduction Videos
                    </h2>
                  </div>
                </div>

                {isEditable && <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8826">
                    Upload 1 short video to introduce yourself and showcase your
                    teaching style
                  </p>}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 overflow-x-hidden" data-id="element-8827">
                  <VideoUploadSlot key={0} index={0} darkMode={darkMode} isEditable={isEditable} videoUrl={teacher.introVideos?.[0]?.url || teacher.introVideos?.[0]} videoTitle={teacher.introVideos?.[0]?.title} onUploadComplete={async url => {
                if (!currentUser?.uid) return;
                try {
                  // Update the introVideos array in Firestore
                  const updatedVideos = [...(teacher.introVideos || [])];
                  updatedVideos[0] = {
                    url,
                    title: updatedVideos[0]?.title || ''
                  };
                  await updateProfile({
                    introVideos: updatedVideos
                  });
                } catch (error) {
                  console.error('Failed to save video URL:', error);
                }
              }} onDelete={async () => {
                if (!currentUser?.uid) return;
                try {
                  const videoToDelete = teacher.introVideos?.[0];
                  const videoUrl = typeof videoToDelete === 'string' ? videoToDelete : videoToDelete?.url;
                  // Delete video from Firebase Storage
                  if (videoUrl) {
                    const {
                      deleteVideo
                    } = await import('../../services/teacherProfileService');
                    await deleteVideo(videoUrl);
                  }
                  // Remove video from Firestore array
                  const updatedVideos = [...(teacher.introVideos || [])];
                  updatedVideos[0] = null;
                  await updateProfile({
                    introVideos: updatedVideos
                  });
                } catch (error) {
                  console.error('Failed to delete video:', error);
                  alert('Failed to delete video. Please try again.');
                }
              }} onUpdateTitle={async title => {
                if (!currentUser?.uid) return;
                try {
                  const updatedVideos = [...(teacher.introVideos || [])];
                  const currentVideo = updatedVideos[0];
                  updatedVideos[0] = {
                    url: typeof currentVideo === 'string' ? currentVideo : currentVideo?.url || '',
                    title
                  };
                  await updateProfile({
                    introVideos: updatedVideos
                  });
                } catch (error) {
                  console.error('Failed to update video title:', error);
                }
              }} data-id="element-8828" />
                </div>
              </section>}

            {/* Credentials */}
            {(isEditable || teacher.credentials && teacher.credentials.length > 0) && <section className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-8829">
                {/* MOBILE: Stacked layout, DESKTOP: Inline layout */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3 sm:gap-0" data-id="element-8830">
                  <div className="flex items-center gap-2" data-id="element-8831">
                    <AwardIcon className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} data-id="element-8832" />
                    <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8833">
                      Credentials & Qualifications
                    </h2>
                  </div>
                  {isEditable && <button onClick={openAddCredentialModal} className="flex items-center gap-2 px-4 py-2 rounded-md border-2 border-[#10B981] text-[#10B981] bg-[#10B981]/10 hover:bg-[#10B981]/20 transition-colors w-full sm:w-auto justify-center" data-id="element-8834">
                      <PlusIcon className="h-4 w-4" data-id="element-8835" />
                      Add Credential
                    </button>}
                </div>
                <div className="space-y-4" data-id="element-8836">
                  {teacher.credentials.map(credential => <div key={credential.id} className={`p-4 rounded-lg border ${darkMode ? 'bg-navy-900/30 border-navy-700' : 'bg-gray-50 border-gray-200'} relative`} data-id="element-8837">
                      <div className="flex items-start gap-3" data-id="element-8838">
                        <div className={`p-2 rounded-lg ${darkMode ? 'bg-navy-700' : 'bg-white'}`} data-id="element-8839">
                          {credential.type === 'degree' && <AwardIcon className="h-5 w-5 text-[#10B981]" data-id="element-8840" />}
                          {credential.type === 'certificate' && <CheckCircleIcon className="h-5 w-5 text-blue-500" data-id="element-8841" />}
                          {credential.type === 'award' && <StarIcon className="h-5 w-5 text-yellow-500" data-id="element-8842" />}
                        </div>
                        <div className="flex-1 pr-12" data-id="element-8843">
                          <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8844">
                            {credential.title}
                          </h4>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8845">
                            {credential.institution} • {credential.year}
                          </p>
                          {credential.description && <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8846">
                              {credential.description}
                            </p>}
                          {credential.certificateUrl && <a href={credential.certificateUrl} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 mt-2 text-sm font-medium text-[#10B981] hover:text-[#059669] transition-colors`} data-id="element-8847">
                              <FileTextIcon className="h-4 w-4" data-id="element-8848" />
                              View Certificate
                              <ExternalLinkIcon className="h-3 w-3" data-id="element-8849" />
                            </a>}
                        </div>
                        {isEditable && <div className="absolute top-2 right-2 flex flex-col gap-2" data-id="element-8850">
                            <button onClick={() => openEditCredentialModal(credential)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'} transition-colors`} data-id="element-8851">
                              <PencilIcon className="h-4 w-4 text-[#10B981]" data-id="element-8852" />
                            </button>
                          </div>}
                      </div>
                      {isEditable && <div className="absolute bottom-2 right-2" data-id="element-8853">
                          <button onClick={() => setDeleteCredentialId(credential.id)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'} transition-colors`} data-id="element-8854">
                            <TrashIcon className="h-4 w-4 text-red-500" data-id="element-8855" />
                          </button>
                        </div>}
                    </div>)}
                </div>
              </section>}

            {/* Student Reviews - Hidden for teacher-only MVP, will re-enable for marketplace */}
            {SHOW_REVIEWS && <section className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-8856">
                <div className="flex items-center justify-between mb-6" data-id="element-8857">
                  <div data-id="element-8858">
                    <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8859">
                      Student Reviews
                    </h2>
                    <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8860">
                      {teacher.reviewCount} reviews • {teacher.rating} average
                      rating
                    </p>
                  </div>
                </div>

                {/* Rating Breakdown */}
                <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-8861">
                  <div className="flex items-center gap-4" data-id="element-8862">
                    <div className="text-center" data-id="element-8863">
                      <div className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8864">
                        {teacher.rating}
                      </div>
                      <StarRating rating={teacher.rating} size="md" data-id="element-8865" />
                      <div className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8866">
                        {teacher.reviewCount} reviews
                      </div>
                    </div>
                    <div className="flex-1 space-y-2" data-id="element-8867">
                      {[5, 4, 3, 2, 1].map(stars => <div key={stars} className="flex items-center gap-2" data-id="element-8868">
                          <span className={`text-sm w-12 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8869">
                            {stars} star
                          </span>
                          <div className={`flex-1 h-2 rounded-full ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8870">
                            <div className="h-full bg-yellow-400 rounded-full" style={{
                        width: stars === 5 ? '85%' : stars === 4 ? '15%' : '0%'
                      }} data-id="element-8871" />
                          </div>
                          <span className={`text-sm w-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8872">
                            {stars === 5 ? '40' : stars === 4 ? '7' : '0'}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Reviews List */}
                <div className="space-y-4" data-id="element-8873">
                  {teacher.reviews.map(review => <ReviewCard key={review.id} review={review} darkMode={darkMode} data-id="element-8874" />)}
                </div>

                <button className={`mt-6 w-full py-3 rounded-lg font-medium transition-colors ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`} data-id="element-8875">
                  Load More Reviews
                </button>
              </section>}
          </div>

          {/* Sidebar (1/3) */}
          <div className="space-y-6" data-id="element-8876">
            {/* Quick Info Card */}
            <div className={`hidden lg:block p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm ${isPublicView ? 'lg:sticky lg:top-4' : ''}`} data-id="element-8877">
              <div className="flex items-center justify-between mb-4" data-id="element-8878">
                <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8879">
                  Quick Info
                </h3>
                {isEditable && <button onClick={() => openEditModal('quickInfo')} className="p-2 rounded-md bg-navy-700/50 hover:bg-navy-700 transition-colors" data-id="element-8880">
                    <PencilIcon className="h-4 w-4 text-[#10B981]" data-id="element-8881" />
                  </button>}
              </div>

              <div className="space-y-4" data-id="element-8882">
                {/* Instruments */}
                <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8883">
                  <p className={`text-base font-bold mb-3 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`} data-id="element-8884">
                    Instruments Taught
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-8885">
                    {teacher.instruments.map(instrument => <span key={instrument} className={`px-3 py-1 rounded-full text-base font-normal ${darkMode ? 'bg-navy-700/50 text-gray-200' : 'bg-gray-100 text-gray-900'}`} data-id="element-8886">
                        {instrument}
                      </span>)}
                  </div>
                </div>

                {/* Teaching Levels */}
                <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8887">
                  <p className={`text-base font-bold mb-3 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`} data-id="element-8888">
                    Teaching Levels
                  </p>
                  <div className="space-y-2" data-id="element-8889">
                    {Object.entries(teacher.teachingLevels).map(([level, teaches]) => teaches && <div key={level} className="flex items-center gap-2" data-id="element-8890">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" data-id="element-8891" />
                            <span className={`text-base font-normal capitalize ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8892">
                              {level}
                            </span>
                          </div>)}
                  </div>
                </div>

                {/* Lesson Formats */}
                <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8893">
                  <p className={`text-base font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8894">
                    Lesson Formats
                  </p>
                  <div className="space-y-2" data-id="element-8895">
                    {teacher.lessonFormats.map(format => <div key={format} className="flex items-center gap-2" data-id="element-8896">
                        <span className={`text-base font-normal ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8897">
                          {format}
                        </span>
                      </div>)}
                  </div>
                </div>

                {/* Pricing */}
                {teacher.pricing.isPublic && <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8898">
                    <p className={`text-base font-bold mb-3 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`} data-id="element-8899">
                      Pricing
                    </p>
                    {teacher.pricing.contactForPricing ? <p className={`text-base font-normal ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8900">
                        Contact for pricing
                      </p> : <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8901">
                        ${teacher.pricing.startingPrice}
                        <span className={`text-base font-normal ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8902">
                          /hour
                        </span>
                      </p>}
                  </div>}

                {/* Languages */}
                <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8903">
                  <p className={`text-base font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8904">
                    Languages Spoken
                  </p>
                  <div className="space-y-2" data-id="element-8905">
                    {teacher.languages.map(lang => <div key={lang.language} className="flex items-center gap-2" data-id="element-8906">
                        <GlobeIcon className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8907" />
                        <span className={`text-base font-normal ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8908">
                          {lang.language}
                        </span>
                        <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8909">
                          ({lang.proficiency})
                        </span>
                      </div>)}
                  </div>
                </div>

                {/* Response Time */}
                <div className={`${darkMode ? 'border-l-2 border-deep-pink/30 pl-4' : ''}`} data-id="element-8910">
                  <p className={`text-base font-bold mb-3 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`} data-id="element-8911">
                    Response Time
                  </p>
                  <div className="flex items-center gap-2" data-id="element-8912">
                    <ClockIcon className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8913" />
                    <span className={`text-base font-normal ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8914">
                      Usually responds within 2 hours
                    </span>
                  </div>
                </div>
              </div>

              {/* Availability Preview */}
              {isPublicView && <>
                  <div className="mt-6 pt-6 border-t border-gray-700" data-id="element-8915">
                    <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8916">
                      Next Available Slots
                    </h4>
                    <div className="space-y-2" data-id="element-8917">
                      {teacher.availableSlots.map((slot, index) => <div key={index} className={`p-3 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} flex items-center justify-between`} data-id="element-8918">
                          <div data-id="element-8919">
                            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8920">
                              {new Date(slot.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                            </p>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8921">
                              {slot.time} • {slot.duration} min
                            </p>
                          </div>
                          <CalendarIcon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8922" />
                        </div>)}
                    </div>
                  </div>

                  {/* Sticky CTA - HIDDEN FOR LAUNCH */}
                  {false && isPublicView && <div className="mt-6 space-y-3" data-id="element-8923">
                      <button className="w-full bg-deep-pink hover:bg-pink-600 text-white py-3 rounded-lg font-semibold transition-colors shadow-lg" data-id="element-8924">
                        Book a Lesson
                      </button>
                      <button className={`w-full ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} py-3 rounded-lg font-semibold transition-colors`} data-id="element-8925">
                        Send Message
                      </button>
                    </div>}
                </>}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Section Modal */}
      {editModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-x-hidden" data-id="element-8926">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`} data-id="element-8927">
            <div className="flex items-center justify-between mb-4" data-id="element-8928">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8929">
                Edit{' '}
                {editingSection === 'about' ? 'About Me' : editingSection === 'philosophy' ? 'Teaching Philosophy' : 'Quick Info'}
              </h3>
              <button onClick={closeEditModal} disabled={isSaving} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-8930">
                <XIcon className="h-5 w-5" data-id="element-8931" />
              </button>
            </div>

            {/* About Me Editor */}
            {editingSection === 'about' && <>
                <textarea value={editValue} onChange={e => setEditValue(e.target.value)} disabled={isSaving} placeholder="Enter your bio..." className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] min-h-[300px] resize-y ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-8932" />
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8933">
                  {editValue.length} characters
                </p>
              </>}

            {/* Teaching Philosophy Editor */}
            {editingSection === 'philosophy' && <>
                <textarea value={editValue} onChange={e => setEditValue(e.target.value)} disabled={isSaving} placeholder="Enter your teaching philosophy..." className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] min-h-[300px] resize-y ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-8934" />
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8935">
                  {editValue.length} characters
                </p>
              </>}

            {/* Quick Info Editor */}
            {editingSection === 'quickInfo' && <div className="space-y-6" data-id="element-8936">
                {/* Debug: Show current state */}
                <div className="hidden" data-id="element-8937">
                  {console.log('Current quickInfoData:', quickInfoData)}
                </div>

                {/* Instruments */}
                <div data-id="element-8938">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8939">
                    Instruments Taught
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2" data-id="element-8940">
                    {quickInfoData.instruments.map(instrument => <span key={instrument} className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${darkMode ? 'bg-navy-700 text-white' : 'bg-gray-100 text-gray-900'}`} data-id="element-8941">
                        {instrument}
                        <button onClick={() => removeInstrument(instrument)} className="hover:text-red-500" data-id="element-8942">
                          <XIcon className="h-3 w-3" data-id="element-8943" />
                        </button>
                      </span>)}
                  </div>
                  <div className="flex gap-2" data-id="element-8944">
                    <input type="text" placeholder="Add instrument..." onKeyPress={e => {
                if (e.key === 'Enter') {
                  addInstrument(e.currentTarget.value);
                  e.currentTarget.value = '';
                }
              }} className={`w-64 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981]`} data-id="element-8945" />
                  </div>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-8946">
                    Press Enter to add
                  </p>
                </div>

                {/* Teaching Levels */}
                <div data-id="element-8947">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8948">
                    Teaching Levels
                  </label>
                  <div className="space-y-2" data-id="element-8949">
                    {(['beginner', 'intermediate', 'advanced'] as const).map(level => <label key={level} className="flex items-center gap-2 cursor-pointer" data-id="element-8950">
                          <input type="checkbox" checked={quickInfoData.teachingLevels[level]} onChange={() => toggleTeachingLevel(level)} className="w-4 h-4 rounded border-gray-300 text-[#10B981] focus:ring-[#10B981]" data-id="element-8951" />
                          <span className={`text-sm capitalize ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8952">
                            {level}
                          </span>
                        </label>)}
                  </div>
                </div>

                {/* Lesson Formats */}
                <div data-id="element-8953">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8954">
                    Lesson Formats
                  </label>
                  <div className="space-y-2" data-id="element-8955">
                    {['In-person', 'Online'].map(format => <label key={format} className="flex items-center gap-2 cursor-pointer" data-id="element-8956">
                        <input type="checkbox" checked={quickInfoData.lessonFormats.includes(format)} onChange={() => toggleLessonFormat(format)} className="w-4 h-4 rounded border-gray-300 text-[#10B981] focus:ring-[#10B981]" data-id="element-8957" />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8958">
                          {format}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Pricing */}
                <div data-id="element-8959">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8960">
                    Pricing
                  </label>

                  {/* Contact for Pricing Checkbox */}
                  <label className="flex items-center gap-2 cursor-pointer mb-3" data-id="element-8961">
                    <input type="checkbox" checked={quickInfoData.pricing.contactForPricing || false} onChange={e => updatePricing('contactForPricing', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-[#10B981] focus:ring-[#10B981]" data-id="element-8962" />
                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8963">
                      Contact for Pricing
                    </span>
                  </label>

                  {/* Price Input - disabled when Contact for Pricing is checked */}
                  {!quickInfoData.pricing.contactForPricing && <div className="flex items-center gap-2" data-id="element-8964">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8965">
                        $
                      </span>
                      <input type="number" value={quickInfoData.pricing.startingPrice || ''} onChange={e => updatePricing('startingPrice', parseInt(e.target.value) || 0)} placeholder="75" className={`w-24 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981]`} data-id="element-8966" />
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8967">
                        /hour
                      </span>
                    </div>}

                  {quickInfoData.pricing.contactForPricing && <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'} italic`} data-id="element-8968">
                      Students will see "Contact for pricing" instead of a
                      specific price
                    </p>}
                </div>

                {/* Languages */}
                <div data-id="element-8969">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8970">
                    Languages Spoken
                  </label>
                  <div className="space-y-2" data-id="element-8971">
                    {quickInfoData.languages.map((lang, index) => <div key={index} className="flex gap-2" data-id="element-8972">
                        <input type="text" value={lang.language} onChange={e => updateLanguage(index, 'language', e.target.value)} placeholder="Language" className={`w-48 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981]`} data-id="element-8973" />
                        <select value={lang.proficiency} onChange={e => updateLanguage(index, 'proficiency', e.target.value)} className={`w-40 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981]`} data-id="element-8974">
                          <option data-id="element-8975">Native</option>
                          <option data-id="element-8976">Fluent</option>
                          <option data-id="element-8977">Conversational</option>
                          <option data-id="element-8978">Basic</option>
                        </select>
                        <button onClick={() => removeLanguage(index)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`} data-id="element-8979">
                          <XIcon className="h-4 w-4" data-id="element-8980" />
                        </button>
                      </div>)}
                  </div>
                  <button onClick={addLanguage} className={`mt-2 text-sm ${darkMode ? 'text-[#10B981]' : 'text-[#10B981]'} hover:underline`} data-id="element-8981">
                    + Add Language
                  </button>
                </div>
              </div>}

            <div className="flex gap-3 justify-end mt-6" data-id="element-8982">
              <button onClick={closeEditModal} disabled={isSaving} className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-8983">
                Cancel
              </button>
              <button onClick={handleSaveSection} disabled={isSaving} className={`px-4 py-2 rounded-md border-2 border-[#10B981] text-[#10B981] bg-[#10B981]/10 hover:bg-[#10B981]/20 transition-colors flex items-center gap-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-8984">
                {isSaving ? <>
                    <div className="h-4 w-4 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" data-id="element-8985" />
                    Saving...
                  </> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>}

      {/* Add Credential Modal */}
      {addCredentialModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-8986">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`} data-id="element-8987">
            <div className="flex items-center justify-between mb-4" data-id="element-8988">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-8989">
                Add Credential
              </h3>
              <button onClick={closeAddCredentialModal} disabled={isSaving} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-8990">
                <XIcon className="h-5 w-5" data-id="element-8991" />
              </button>
            </div>

            <div className="space-y-4" data-id="element-8992">
              {/* Type */}
              <div data-id="element-8993">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-8994">
                  Type <span className="text-red-500" data-id="element-8995">*</span>
                </label>
                <select value={newCredential.type} onChange={e => handleCredentialTypeChange(e.target.value as any)} disabled={isSaving} className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-8996">
                  <option value="degree" data-id="element-8997">Degree</option>
                  <option value="certificate" data-id="element-8998">Certificate</option>
                  <option value="award" data-id="element-8999">Award</option>
                </select>
              </div>

              {/* Title */}
              <div data-id="element-9000">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9001">
                  Title <span className="text-red-500" data-id="element-9002">*</span>
                </label>
                <input type="text" value={newCredential.title} onChange={e => setNewCredential(prev => ({
              ...prev,
              title: e.target.value
            }))} disabled={isSaving} placeholder="e.g., Master of Music in Piano Performance" className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9003" />
              </div>

              {/* Institution */}
              <div data-id="element-9004">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9005">
                  Institution <span className="text-red-500" data-id="element-9006">*</span>
                </label>
                <input type="text" value={newCredential.institution} onChange={e => setNewCredential(prev => ({
              ...prev,
              institution: e.target.value
            }))} disabled={isSaving} placeholder="e.g., Juilliard School" className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9007" />
              </div>

              {/* Year Awarded */}
              <div data-id="element-9008">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9009">
                  Year Awarded{' '}
                  {(newCredential.type === 'degree' || newCredential.type === 'certificate') && <span className="text-red-500" data-id="element-9010">*</span>}
                  {newCredential.type === 'award' && <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9011">
                      {' '}
                      (Optional)
                    </span>}
                </label>
                <input type="text" value={newCredential.year} onChange={e => handleYearChange(e.target.value)} onBlur={() => {
              const error = validateYear(newCredential.year, newCredential.type);
              setYearError(error);
            }} disabled={isSaving} placeholder="e.g., 2005" maxLength={4} className={`w-full px-4 py-2 rounded-lg border ${yearError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : darkMode ? 'border-navy-700 focus:ring-[#10B981] focus:border-[#10B981]' : 'border-gray-300 focus:ring-[#10B981] focus:border-[#10B981]'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-400' : 'bg-white text-gray-900 placeholder-gray-500'} focus:ring-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9012" />
                {yearError ? <p className="mt-1 text-xs text-red-500" data-id="element-9013">{yearError}</p> : <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9014">
                    Example: 2005 – the year your degree, certificate, or award
                    was granted.
                  </p>}
              </div>

              {/* Description */}
              <div data-id="element-9015">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9016">
                  Description
                </label>
                <textarea value={newCredential.description} onChange={e => setNewCredential(prev => ({
              ...prev,
              description: e.target.value
            }))} disabled={isSaving} placeholder="e.g., Specialized in classical piano performance and pedagogy" className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] min-h-[100px] resize-y ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9017" />
              </div>

              {/* Certificate Upload */}
              <div data-id="element-9018">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9019">
                  Upload Certificate (Optional)
                </label>
                <div className={`border-2 border-dashed rounded-lg p-4 text-center ${darkMode ? 'border-navy-700 bg-navy-900/30' : 'border-gray-300 bg-gray-50'}`} data-id="element-9020">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => {
                if (e.target.files && e.target.files[0]) {
                  setNewCredentialFile(e.target.files[0]);
                }
              }} disabled={isSaving} className="hidden" id="new-credential-file" data-id="element-9021" />
                  <label htmlFor="new-credential-file" className={`cursor-pointer ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9022">
                    <UploadIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9023" />
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9024">
                      {newCredentialFile ? newCredentialFile.name : 'Click to upload PDF, JPG, or PNG'}
                    </p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-9025">
                      Max file size: 10MB
                    </p>
                  </label>
                  {newCredentialFile && <button onClick={() => setNewCredentialFile(null)} disabled={isSaving} className={`mt-2 text-sm text-red-500 hover:text-red-600 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9026">
                      Remove file
                    </button>}
                </div>
                {uploadProgress > 0 && uploadProgress < 100 && <div className="mt-2" data-id="element-9027">
                    <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-9028">
                      <div className="bg-[#10B981] h-2 rounded-full transition-all" style={{
                  width: `${uploadProgress}%`
                }} data-id="element-9029" />
                    </div>
                    <p className="text-xs text-center mt-1 text-gray-500" data-id="element-9030">
                      Uploading... {uploadProgress}%
                    </p>
                  </div>}
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6" data-id="element-9031">
              <button onClick={closeAddCredentialModal} disabled={isSaving} className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9032">
                Cancel
              </button>
              <button onClick={handleSaveCredential} disabled={isSaving || !!yearError} className={`px-4 py-2 rounded-md border-2 border-[#10B981] text-[#10B981] bg-[#10B981]/10 hover:bg-[#10B981]/20 transition-colors flex items-center gap-2 ${isSaving || yearError ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9033">
                {isSaving ? <>
                    <div className="h-4 w-4 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" data-id="element-9034" />
                    Saving...
                  </> : 'Save Credential'}
              </button>
            </div>
          </div>
        </div>}

      {/* Edit Credential Modal */}
      {editCredentialModalOpen && editingCredential && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-9035">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`} data-id="element-9036">
            <div className="flex items-center justify-between mb-4" data-id="element-9037">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9038">
                Edit Credential
              </h3>
              <button onClick={closeEditCredentialModal} disabled={isSaving} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9039">
                <XIcon className="h-5 w-5" data-id="element-9040" />
              </button>
            </div>

            <div className="space-y-4" data-id="element-9041">
              {/* Type */}
              <div data-id="element-9042">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9043">
                  Type <span className="text-red-500" data-id="element-9044">*</span>
                </label>
                <select value={editingCredential.type} onChange={e => setEditingCredential(prev => ({
              ...prev,
              type: e.target.value as any
            }))} disabled={isSaving} className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9045">
                  <option value="degree" data-id="element-9046">Degree</option>
                  <option value="certificate" data-id="element-9047">Certificate</option>
                  <option value="award" data-id="element-9048">Award</option>
                </select>
              </div>

              {/* Title */}
              <div data-id="element-9049">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9050">
                  Title <span className="text-red-500" data-id="element-9051">*</span>
                </label>
                <input type="text" value={editingCredential.title} onChange={e => setEditingCredential(prev => ({
              ...prev,
              title: e.target.value
            }))} disabled={isSaving} placeholder="e.g., Master of Music in Piano Performance" className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9052" />
              </div>

              {/* Institution */}
              <div data-id="element-9053">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9054">
                  Institution <span className="text-red-500" data-id="element-9055">*</span>
                </label>
                <input type="text" value={editingCredential.institution} onChange={e => setEditingCredential(prev => ({
              ...prev,
              institution: e.target.value
            }))} disabled={isSaving} placeholder="e.g., Juilliard School" className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9056" />
              </div>

              {/* Year */}
              <div data-id="element-9057">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9058">
                  Year <span className="text-red-500" data-id="element-9059">*</span>
                </label>
                <input type="text" value={editingCredential.year} onChange={e => setEditingCredential(prev => ({
              ...prev,
              year: e.target.value
            }))} disabled={isSaving} placeholder="e.g., 2008" className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9060" />
                {yearError && <p className={`text-sm mt-1 ${darkMode ? 'text-red-500' : 'text-red-500'}`} data-id="element-9061">
                    {yearError}
                  </p>}
              </div>

              {/* Description */}
              <div data-id="element-9062">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9063">
                  Description
                </label>
                <textarea value={editingCredential.description} onChange={e => setEditingCredential(prev => ({
              ...prev,
              description: e.target.value
            }))} disabled={isSaving} placeholder="e.g., Specialized in classical piano performance and pedagogy" className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] min-h-[100px] resize-y ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9064" />
              </div>

              {/* Certificate Upload */}
              <div data-id="element-9065">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9066">
                  Upload Certificate (Optional)
                </label>
                {editingCredential.certificateUrl && !editCredentialFile && <div className={`mb-3 p-3 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} flex items-center justify-between`} data-id="element-9067">
                    <div className="flex items-center gap-2" data-id="element-9068">
                      <FileTextIcon className="h-4 w-4 text-[#10B981]" data-id="element-9069" />
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9070">
                        Current certificate
                      </span>
                    </div>
                    <a href={editingCredential.certificateUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#10B981] hover:text-[#059669] flex items-center gap-1" data-id="element-9071">
                      View
                      <ExternalLinkIcon className="h-3 w-3" data-id="element-9072" />
                    </a>
                  </div>}
                <div className={`border-2 border-dashed rounded-lg p-4 text-center ${darkMode ? 'border-navy-700 bg-navy-900/30' : 'border-gray-300 bg-gray-50'}`} data-id="element-9073">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => {
                if (e.target.files && e.target.files[0]) {
                  setEditCredentialFile(e.target.files[0]);
                  setUploadProgress(0);
                  setUploadStatus('');
                }
              }} disabled={isSaving} className="hidden" id="edit-credential-file" data-id="element-9074" />
                  <label htmlFor="edit-credential-file" className={`cursor-pointer ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9075">
                    <UploadIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9076" />
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9077">
                      {editCredentialFile ? editCredentialFile.name : editingCredential.certificateUrl ? 'Click to replace certificate' : 'Click to upload PDF, JPG, or PNG'}
                    </p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-9078">
                      Max file size: 10MB
                    </p>
                  </label>
                  {editCredentialFile && <button onClick={() => {
                setEditCredentialFile(null);
                setUploadProgress(0);
                setUploadStatus('');
              }} disabled={isSaving} className={`mt-2 text-sm text-red-500 hover:text-red-600 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9079">
                      Remove file
                    </button>}
                </div>
                {uploadProgress > 0 && uploadProgress < 100 && <div className="mt-3" data-id="element-9080">
                    <div className="flex items-center justify-between mb-1" data-id="element-9081">
                      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-9082">
                        {uploadStatus || 'Uploading...'}
                      </span>
                      <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9083">
                        {uploadProgress}%
                      </span>
                    </div>
                    <div className={`w-full h-2 rounded-full overflow-hidden ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-9084">
                      <div className="h-full bg-[#10B981] transition-all duration-300 ease-out" style={{
                  width: `${uploadProgress}%`
                }} data-id="element-9085" />
                    </div>
                  </div>}
                {uploadProgress === 100 && uploadStatus && <div className="mt-2 flex items-center text-sm text-green-500" data-id="element-9086">
                    <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-9087" />
                    {uploadStatus}
                  </div>}
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6" data-id="element-9088">
              <button onClick={closeEditCredentialModal} disabled={isSaving} className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9089">
                Cancel
              </button>
              <button onClick={handleUpdateCredential} disabled={isSaving} className={`px-4 py-2 rounded-md border-2 border-[#10B981] text-[#10B981] bg-[#10B981]/10 hover:bg-[#10B981]/20 transition-colors flex items-center gap-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9090">
                {isSaving ? <>
                    <div className="h-4 w-4 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" data-id="element-9091" />
                    Updating...
                  </> : 'Update Credential'}
              </button>
            </div>
          </div>
        </div>}

      {/* Delete Credential Confirmation Modal */}
      {deleteCredentialId && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-9092">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full`} data-id="element-9093">
            <div className="flex items-center justify-between mb-4" data-id="element-9094">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9095">
                Delete Credential
              </h3>
              <button onClick={() => setDeleteCredentialId(null)} disabled={isSaving} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9096">
                <XIcon className="h-5 w-5" data-id="element-9097" />
              </button>
            </div>

            <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9098">
              Are you sure you want to delete this credential? This action
              cannot be undone.
            </p>

            <div className="flex gap-3 justify-end" data-id="element-9099">
              <button onClick={() => setDeleteCredentialId(null)} disabled={isSaving} className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9100">
                Cancel
              </button>
              <button onClick={() => handleDeleteCredential(deleteCredentialId)} disabled={isSaving} className={`px-4 py-2 rounded-md bg-red-500 hover:bg-red-600 text-white transition-colors flex items-center gap-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9101">
                {isSaving ? <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-9102" />
                    Deleting...
                  </> : 'Delete'}
              </button>
            </div>
          </div>
        </div>}

      {/* Edit Banner Image Modal */}
      {editBannerModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-9103">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`} data-id="element-9104">
            <div className="flex items-center justify-between mb-4" data-id="element-9105">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9106">
                Edit Banner Image
              </h3>
              <button onClick={closeEditBannerModal} disabled={isSaving} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9107">
                <XIcon className="h-5 w-5" data-id="element-9108" />
              </button>
            </div>

            <div className="space-y-4" data-id="element-9109">
              {/* Current/Preview Banner */}
              <div data-id="element-9110">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9111">
                  Banner Preview
                </label>
                <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-dashed ${darkMode ? 'border-navy-700' : 'border-gray-300'}" data-id="element-9112">
                  {bannerPreviewUrl ? <img src={bannerPreviewUrl} alt="Banner preview" className="w-full h-full object-cover" data-id="element-9113" /> : <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'bg-navy-900/50' : 'bg-gray-100'}`} data-id="element-9114">
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9115">
                        No banner image
                      </p>
                    </div>}
                </div>
              </div>

              {/* Upload Section */}
              <div data-id="element-9116">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9117">
                  Upload New Banner
                </label>
                <div onClick={() => bannerFileInputRef.current?.click()} className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${darkMode ? 'border-navy-700 hover:border-navy-600 bg-navy-900/30 hover:bg-navy-900/50' : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9118">
                  <UploadIcon className={`h-12 w-12 mx-auto mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9119" />
                  <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9120">
                    Click to upload or drag and drop
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9121">
                    PNG, JPG up to 5MB
                  </p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9122">
                    Recommended: 1200x400px
                  </p>
                  <input ref={bannerFileInputRef} type="file" accept="image/*" onChange={handleBannerFileSelect} disabled={isSaving} className="hidden" data-id="element-9123" />
                </div>
                {newBannerImage && <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-9124">
                    Selected: {newBannerImage.name}
                  </p>}
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6" data-id="element-9125">
              <button onClick={closeEditBannerModal} disabled={isSaving} className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9126">
                Cancel
              </button>
              <button onClick={handleSaveBannerImage} disabled={isSaving || !newBannerImage} className={`px-4 py-2 rounded-md border-2 border-[#10B981] text-[#10B981] bg-[#10B981]/10 hover:bg-[#10B981]/20 transition-colors flex items-center gap-2 ${isSaving || !newBannerImage ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-9127">
                {isSaving ? <>
                    <div className="h-4 w-4 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" data-id="element-9128" />
                    Uploading...
                  </> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>}

      {/* Share Profile Modal */}
      {showShareModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-9129">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full shadow-2xl`} data-id="element-9130">
            <div className="flex items-center justify-between mb-4" data-id="element-9131">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9132">
                Share Your Profile
              </h3>
              <button onClick={() => setShowShareModal(false)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} transition-colors`} data-id="element-9133">
                <XIcon className="h-5 w-5" data-id="element-9134" />
              </button>
            </div>

            <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-9135">
              Share your teaching profile with students and colleagues
            </p>

            <div className="space-y-3" data-id="element-9136">
              {/* Copy Link Option */}
              <button onClick={handleCopyLink} className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${darkMode ? 'border-navy-700 hover:border-[#10B981] bg-navy-900/30 hover:bg-navy-900/50' : 'border-gray-200 hover:border-[#10B981] bg-gray-50 hover:bg-gray-100'}`} data-id="element-9137">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#10B981]/20" data-id="element-9138">
                  <CopyIcon className="h-5 w-5 text-[#10B981]" data-id="element-9139" />
                </div>
                <div className="flex-1 text-left" data-id="element-9140">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9141">
                    Copy Link
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-9142">
                    Copy profile URL to clipboard
                  </p>
                </div>
              </button>

              {/* Email Link Option */}
              <button onClick={handleEmailLink} className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${darkMode ? 'border-navy-700 hover:border-[#10B981] bg-navy-900/30 hover:bg-navy-900/50' : 'border-gray-200 hover:border-[#10B981] bg-gray-50 hover:bg-gray-100'}`} data-id="element-9143">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/20" data-id="element-9144">
                  <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-9145">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-9146" />
                  </svg>
                </div>
                <div className="flex-1 text-left" data-id="element-9147">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9148">
                    Email Link
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-9149">
                    Open email client to share
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>}

      {/* Copy Success Toast */}
      {showCopyToast && <div className="fixed bottom-4 right-4 z-50 animate-fade-in" data-id="element-9150">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-9151">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500/20" data-id="element-9152">
              <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-9153" />
            </div>
            <div data-id="element-9154">
              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9155">
                {copyToastMessage}
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-9156">
                Share your profile with students
              </p>
            </div>
          </div>
        </div>}
    </div>;
}
// Main Page Component
export function TeacherPublicProfilePage() {
  const {
    theme
  } = useTheme();
  const darkMode = theme === 'dark';
  const {
    currentUser
  } = useAuth();
  const params = useParams<{
    teacherId?: string;
    id?: string;
  }>();
  const [searchParams] = useSearchParams();
  // Handle both route parameter names (teacherId and id)
  const teacherId = params.teacherId || params.id;
  // Determine mode
  const isPublicView = !!teacherId;
  const isPreviewMode = searchParams.get('preview') === 'true';
  const mode = isPublicView ? 'public' : isPreviewMode ? 'preview' : 'edit';
  // Determine whose profile to show
  const profileId = teacherId || currentUser?.uid || '';
  // Public view (standalone, no layout)
  if (isPublicView) {
    return <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-9157">
        <ProfileContent mode="public" teacherId={profileId} darkMode={darkMode} data-id="element-9158" />
      </div>;
  }
  // Dashboard view (with TeacherLayout)
  return <TeacherLayout data-id="element-9159">
      <ProfileContent mode={mode as 'edit' | 'preview'} teacherId={profileId} darkMode={darkMode} data-id="element-9160" />
    </TeacherLayout>;
}